import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileDown, Printer, Mail } from "lucide-react";
import { getProgram, getCoachingAthlete, getWeekCompletion } from "../../lib/api";
import { BrandBanner, BrandFooter } from "../../components/ReportFrame";
import { RichTextView, looksLikeHtml, decodeEntities } from "../../components/RichTextEditor";
import { workoutTypeLabel, workoutTypeColor, formatSegmentValue, strengthBlockLabel, weekDateRange, formatDateRange, formatStrengthIntensity, weekVolume, formatVolume } from "../../lib/coaching";
import EmailReportModal from "../../components/EmailReportModal";
import { toast } from "sonner";
import { linkify, shortLinkLabel } from "../../lib/linkify";

function formatLongDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function SegmentLine({ seg, indent = false }) {
  if (!seg) return null;
  const meta = formatSegmentValue(seg);
  return (
    <div
      className={`flex gap-2 text-[10px] leading-relaxed ${
        indent ? "pl-4" : ""
      }`}
    >
      <span className="font-semibold tracking-[0.05em] text-[var(--tir-ink)] min-w-[70px]">
        {seg.label || "Segment"}:
      </span>
      <span className="font-mono-tir text-[var(--tir-ink)]">{meta || "—"}</span>
      {seg.notes && (
        <span className="italic text-[var(--tir-muted)]">· {linkify(seg.notes)}</span>
      )}
    </div>
  );
}

function GroupBlock({ seg }) {
  return (
    <div className="border-l-2 border-[var(--tir-burgundy)] pl-2 py-0.5 my-0.5">
      <div className="text-[10px] font-semibold text-[var(--tir-burgundy)] tracking-[0.08em] uppercase mb-0.5">
        {seg.label || "Main Set"} · repeat {seg.repeat_count || 1}×
      </div>
      {(seg.items || []).map((it) => (
        <SegmentLine key={it.id} seg={it} indent />
      ))}
    </div>
  );
}

function StrengthBlockRender({ block }) {
  const header = [strengthBlockLabel(block.kind)];
  if (block.rounds) header.push(`${block.rounds} rounds`);
  if (block.time_cap_minutes) header.push(`${block.time_cap_minutes} min cap`);
  return (
    <div className="border-l-2 border-[var(--tir-burgundy)] pl-2 py-0.5 my-1">
      <div className="text-[10px] font-semibold text-[var(--tir-burgundy)] tracking-[0.08em] uppercase mb-0.5">
        {(block.label ? `${block.label} · ` : "") + header.join(" · ")}
      </div>
      {block.notes && (
        <div className="text-[9px] italic text-[var(--tir-muted)] mb-1">
          {linkify(block.notes)}
        </div>
      )}
      <table className="w-full text-[9px] border-collapse">
        <thead>
          <tr className="border-b border-[var(--tir-border)] text-[var(--tir-muted)] uppercase tracking-[0.08em]">
            <th className="text-left font-semibold py-0.5 pr-2">Exercise</th>
            <th className="text-center font-semibold py-0.5 px-1 w-10">Sets</th>
            <th className="text-center font-semibold py-0.5 px-1 w-12">Reps</th>
            <th className="text-center font-semibold py-0.5 px-1 w-14">Intensity</th>
            <th className="text-center font-semibold py-0.5 px-1 w-10">Rest</th>
            <th className="text-left font-semibold py-0.5 pl-2">Notes</th>
          </tr>
        </thead>
        <tbody>
          {(block.exercises || []).map((ex) => (
            <tr key={ex.id} className="border-b border-[var(--tir-border)]/60">
              <td className="py-0.5 pr-2 text-[var(--tir-ink)] font-medium">
                {ex.name || "—"}
              </td>
              <td className="text-center py-0.5 px-1 font-mono-tir">{ex.sets || ""}</td>
              <td className="text-center py-0.5 px-1 font-mono-tir">{ex.reps || ""}</td>
              <td className="text-center py-0.5 px-1 font-mono-tir">{formatStrengthIntensity(ex)}</td>
              <td className="text-center py-0.5 px-1 font-mono-tir">{ex.rest || ""}</td>
              <td className="py-0.5 pl-2 italic text-[var(--tir-muted)]">
                {ex.notes ? linkify(ex.notes) : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WorkoutBlock({ workout }) {
  if (!workout) return null;
  const label = workout.title?.trim() || workoutTypeLabel(workout.type);
  const color = workoutTypeColor(workout.type);
  const slot = (workout.slot || "any").toUpperCase();
  const segments = workout.segments || [];
  const strengthBlocks = workout.strength_blocks || [];
  const legacyStructure = !segments.length && !strengthBlocks.length && workout.structure;
  return (
    <div
      className="border border-[var(--tir-border)] bg-white pl-2.5 pr-2.5 py-2"
      style={{ borderLeft: `4px solid ${color}` }}
    >
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          {workout.slot && workout.slot !== "any" && (
            <span className="text-[8px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)]">
              {slot}
            </span>
          )}
          <span className="text-[11px] font-semibold text-[var(--tir-ink)] uppercase tracking-[0.08em]">
            {label}
          </span>
        </div>
        {workout.duration_or_distance && (
          <span className="text-[10px] font-mono-tir text-[var(--tir-ink)] whitespace-nowrap">
            {workout.duration_or_distance}
          </span>
        )}
      </div>
      {strengthBlocks.length > 0 && (
        <div className="space-y-1.5">
          {strengthBlocks.map((b) => (
            <StrengthBlockRender key={b.id} block={b} />
          ))}
        </div>
      )}
      {segments.length > 0 && (
        <div className="space-y-0.5">
          {segments.map((seg) =>
            seg.kind === "group" ? (
              <GroupBlock key={seg.id} seg={seg} />
            ) : (
              <SegmentLine key={seg.id} seg={seg} />
            )
          )}
        </div>
      )}
      {legacyStructure && (
        <pre
          className="whitespace-pre-wrap font-mono-tir text-[10px] leading-snug text-[var(--tir-ink)] mt-1.5"
          style={{ fontFamily: "inherit" }}
        >
          {workout.structure}
        </pre>
      )}
      {workout.add_strides && (
        <div className="text-[10px] text-[var(--tir-burgundy)] mt-1">
          + {workout.strides_count || ""} {workout.strides_kind === "hill_surges" ? "hill surges" : "strides"} in the last half
        </div>
      )}
      {(workout.external_link || workout.audio_link) && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[10px]">
          {workout.external_link && (
            <a
              href={workout.external_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--tir-burgundy)] underline font-mono-tir break-all"
              title={workout.external_link}
            >
              ↗ {shortLinkLabel(workout.external_link)}
            </a>
          )}
          {workout.audio_link && (
            <a
              href={workout.audio_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--tir-burgundy)] underline font-mono-tir break-all"
              title={workout.audio_link}
            >
              ♪ {shortLinkLabel(workout.audio_link)}
            </a>
          )}
        </div>
      )}
      {workout.session_note && (
        looksLikeHtml(workout.session_note) ? (
          <RichTextView
            html={workout.session_note}
            className="text-[10px] italic text-[var(--tir-muted)] mt-1 leading-snug"
          />
        ) : (
          <div className="text-[10px] italic text-[var(--tir-muted)] mt-1 leading-snug">
            {linkify(decodeEntities(workout.session_note))}
          </div>
        )
      )}
    </div>
  );
}

export default function ProgramWeekReportPage() {
  const { id, weekNumber } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [program, setProgram] = useState(null);
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [completion, setCompletion] = useState(null);
  const pageRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const p = await getProgram(id);
        setProgram(p);
        if (p.athlete_id) {
          try {
            const a = await getCoachingAthlete(p.athlete_id);
            setAthlete(a);
          } catch {
            /* ignore */
          }
        }
        try {
          const c = await getWeekCompletion(id, parseInt(weekNumber, 10));
          setCompletion(c);
        } catch {
          /* ignore — completion is optional */
        }
      } catch {
        toast.error("Couldn't load program");
        navigate("/coaching");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, weekNumber, navigate]);

  const week = useMemo(() => {
    if (!program) return null;
    const num = parseInt(weekNumber, 10);
    return program.weeks.find((w) => w.week_number === num) || null;
  }, [program, weekNumber]);

  // Auto-open email modal when ?send=1
  useEffect(() => {
    if (!loading && program && week && searchParams.get("send") === "1") {
      setEmailOpen(true);
    }
  }, [loading, program, week, searchParams]);

  const closeEmail = () => {
    setEmailOpen(false);
    if (searchParams.get("send")) {
      searchParams.delete("send");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const buildPdf = async () => {
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import("html2canvas"),
      import("jspdf"),
    ]);
    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pageWidth = 210;
    const pageHeight = 297;
    const node = pageRef.current;
    // Collect link rects BEFORE rasterization so we can overlay clickable
    // annotations onto the rendered PDF page.
    const nodeRect = node.getBoundingClientRect();
    const linkData = Array.from(node.querySelectorAll("a[href]"))
      .map((a) => {
        const r = a.getBoundingClientRect();
        if (!r.width || !r.height) return null;
        return {
          href: a.getAttribute("href"),
          fx: (r.left - nodeRect.left) / nodeRect.width,
          fy: (r.top - nodeRect.top) / nodeRect.height,
          fw: r.width / nodeRect.width,
          fh: r.height / nodeRect.height,
        };
      })
      .filter(Boolean);
    const canvas = await html2canvas(node, { backgroundColor: "#ffffff", scale: 2, useCORS: true });
    const img = canvas.toDataURL("image/png");
    const ratio = canvas.width / canvas.height;
    let w = pageWidth;
    let h = w / ratio;
    if (h > pageHeight) {
      h = pageHeight;
      w = h * ratio;
    }
    const x = (pageWidth - w) / 2;
    const y = (pageHeight - h) / 2;
    pdf.addImage(img, "PNG", x, y, w, h, undefined, "FAST");
    for (const ld of linkData) {
      if (!ld.href) continue;
      const href = ld.href.startsWith("http") ? ld.href : ld.href.startsWith("www.") ? `https://${ld.href}` : ld.href;
      pdf.link(x + ld.fx * w, y + ld.fy * h, ld.fw * w, ld.fh * h, { url: href });
    }
    const filename = `${(athlete?.name || "athlete").replace(/\s+/g, "_")}-Week-${week?.week_number || ""}.pdf`;
    return { blob: pdf.output("blob"), filename };
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await buildPdf();
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Week PDF exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading || !program || !week) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-center text-[var(--tir-muted)]">
        Loading week…
      </div>
    );
  }

  const athleteName = athlete?.name || "Athlete";
  const phase = week.phase_label;
  const weekLabel = phase
    ? `${phase} · Week ${week.week_number}`
    : `Week ${week.week_number}`;

  return (
    <div
      data-testid="program-week-report-page"
      className="min-h-screen pb-20"
    >
      <div className="no-print sticky top-0 z-40 bg-[var(--tir-ivory)]/95 backdrop-blur border-b border-[var(--tir-border)]">
        <div className="max-w-[900px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link
            to={`/coaching/programs/${id}/edit`}
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors"
          >
            <ArrowLeft size={14} /> Back to editor
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.print()}
              data-testid="print-week-btn"
              className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors"
            >
              <Printer size={14} /> Print
            </button>
            <button
              onClick={exportPDF}
              disabled={exporting}
              data-testid="download-week-btn"
              className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors disabled:opacity-50"
            >
              <FileDown size={14} /> {exporting ? "Exporting…" : "Download"}
            </button>
            <button
              onClick={() => setEmailOpen(true)}
              data-testid="email-week-btn"
              className="inline-flex items-center gap-2 bg-[var(--tir-burgundy)] text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors"
            >
              <Mail size={14} /> Send to athlete
            </button>
          </div>
        </div>
      </div>

      <EmailReportModal
        open={emailOpen}
        onClose={closeEmail}
        athlete={athlete || { id: program.athlete_id, name: athleteName, email: "" }}
        testId={null}
        testDate={week.start_date || formatLongDate(new Date().toISOString())}
        defaultFilename={`${(athleteName || "athlete").replace(/\s+/g, "_")}-Week-${week.week_number}.pdf`}
        generatePdf={async () => (await buildPdf()).blob}
        reportTypeLabel="coaching_week"
        extraTokens={{ week_number: week.week_number, phase: week.phase_label || "" }}
      />

      <div className="pt-8 px-4">
        <div ref={pageRef} className="print-page" data-testid="week-report-sheet">
          <BrandBanner
            athleteName={athleteName}
            testDate={week.start_date || ""}
            headline={program.title}
          />
          <div className="px-10 pt-7 pb-14">
            <div className="grid grid-cols-12 gap-6 border-b border-[var(--tir-border)] pb-4 mb-5">
              <div className="col-span-3">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">
                  {week.is_recovery ? "Recovery week" : "Training week"}
                </div>
                <div className="font-mono-tir text-sm mt-1">{weekLabel}</div>
                {(() => {
                  const [ws, we] = weekDateRange(program.start_date, week.week_number);
                  return ws && we ? (
                    <div className="font-mono-tir text-[11px] text-[var(--tir-muted)] mt-0.5">
                      {formatDateRange(ws, we)}
                    </div>
                  ) : null;
                })()}
              </div>
              <div className="col-span-3">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">
                  Weekly volume
                </div>
                <div className="font-mono-tir text-sm mt-1 text-[var(--tir-burgundy)]">
                  {formatVolume(weekVolume(week)) || "—"}
                </div>
              </div>
              {completion && completion.available && completion.prescribed > 0 ? (
                <div className="col-span-3">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">
                    Week completion
                  </div>
                  <div
                    className="font-mono-tir text-sm mt-1 inline-flex items-center gap-1.5"
                    data-testid="pdf-week-completion"
                  >
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{
                        background:
                          completion.completion_pct >= 0.8
                            ? "#2f7d4a"
                            : completion.completion_pct >= 0.5
                            ? "#e6a35a"
                            : "var(--tir-burgundy)",
                      }}
                    />
                    {Math.round(completion.completion_pct * 100)}% (
                    {completion.matched}/{completion.prescribed})
                  </div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)] mt-1">
                    {completion.matched}M · {completion.partial}P ·{" "}
                    {completion.missed}X
                  </div>
                </div>
              ) : (
                <div className="col-span-3" />
              )}
              <div className="col-span-3">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">
                  Program goal
                </div>
                <div className="font-mono-tir text-sm mt-1">
                  {program.goal || program.target_race || program.title}
                </div>
              </div>
            </div>

            {week.coach_note && (
              <div className="mb-6 border-l-2 border-[var(--tir-burgundy)] pl-4 py-1">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-burgundy)] font-semibold mb-1">
                  Coach's note
                </div>
                {looksLikeHtml(week.coach_note) ? (
                  <RichTextView
                    html={week.coach_note}
                    className="text-sm text-[var(--tir-ink)] leading-relaxed"
                  />
                ) : (
                  <p className="text-sm text-[var(--tir-ink)] leading-relaxed whitespace-pre-wrap">
                    {linkify(week.coach_note)}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-3">
              {week.days.map((day, dayIdx) => {
                const dayComp =
                  completion && completion.available && completion.days
                    ? completion.days[dayIdx]
                    : null;
                const dayStatus = dayComp?.status;
                const dayActivities = dayComp?.activities || [];
                const dot = (() => {
                  if (!dayStatus || dayStatus === "rest") return null;
                  const map = {
                    matched: { bg: "#2f7d4a", label: "Done", char: "✓" },
                    partial: { bg: "#e6a35a", label: "Partial", char: "~" },
                    missed: { bg: "var(--tir-burgundy)", label: "Missed", char: "✗" },
                    unmatched_only: { bg: "var(--tir-muted)", label: "Activity only", char: "•" },
                  };
                  return map[dayStatus];
                })();
                return (
                  <div
                    key={day.id}
                    className="grid grid-cols-12 gap-3 border-b border-[var(--tir-border)] pb-3 last:border-b-0"
                  >
                    <div className="col-span-2 pt-1">
                      <div className="flex items-center gap-1.5">
                        <div className="font-serif-display text-xl text-[var(--tir-ink)] leading-tight">
                          {day.label}
                        </div>
                        {dot && (
                          <span
                            className="inline-flex items-center justify-center h-4 w-4 rounded-full text-white text-[10px] font-bold"
                            style={{ background: dot.bg }}
                            title={dot.label}
                            data-testid={`pdf-day-status-${dayStatus}`}
                          >
                            {dot.char}
                          </span>
                        )}
                      </div>
                      {dayActivities.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {dayActivities.map((a) => {
                            const km = (a.distance_m || 0) / 1000;
                            const mins = Math.round((a.moving_time_s || 0) / 60);
                            return (
                              <div
                                key={a.id}
                                className="text-[8px] tracking-[0.05em] text-[var(--tir-muted)] font-mono-tir leading-tight"
                              >
                                {km > 0 ? `${km.toFixed(1)}k` : ""}
                                {km > 0 && mins > 0 ? " · " : ""}
                                {mins > 0 ? `${mins}m` : ""}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="col-span-10 space-y-2">
                      {(day.workouts || []).length === 0 ? (
                        <div className="text-[11px] italic text-[var(--tir-muted)] py-1.5">
                          Rest day or flexible
                        </div>
                      ) : (
                        day.workouts.map((w) => <WorkoutBlock key={w.id} workout={w} />)
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <BrandFooter />
        </div>
      </div>
    </div>
  );
}
